import type { EmojiEntry, EmojiGroup, EmojiLocale } from './types';

interface RawSkin {
  emoji: string;
  tone?: number | number[];
}

interface RawEmoji {
  emoji: string;
  label: string;
  tags?: string[];
  group?: number;
  subgroup?: number;
  skins?: RawSkin[];
}

interface RawMessage {
  key: string;
  message: string;
  order: number;
}

interface RawMessages {
  groups: RawMessage[];
  subgroups: RawMessage[];
}

interface RawGroupMeta {
  groups: Record<string, string>;
  hierarchy: Record<string, number[]>;
}

const SKIN_TONE_COMPONENT_GROUP = 2;

const groupCache = new Map<EmojiLocale, EmojiGroup[]>();
const labelCache = new Map<string, string>();

async function fetchRaw(locale: EmojiLocale): Promise<{
  data: RawEmoji[];
  messages: RawMessages;
  meta: RawGroupMeta;
}> {
  // Static branches let Vite resolve and chunk each JSON dataset; a templated
  // import like `import(\`.../${locale}/data.json\`)` would silently leave the
  // datasets out of the build.
  const [{ data, messages }, meta] = await Promise.all([
    locale === 'en' ? loadEn() : loadFi(),
    import('emojibase-data/meta/groups.json').then((m) => m.default as RawGroupMeta),
  ]);
  return { data, messages, meta };
}

async function loadFi(): Promise<{ data: RawEmoji[]; messages: RawMessages }> {
  const [data, messages] = await Promise.all([
    import('emojibase-data/fi/data.json').then((m) => m.default as RawEmoji[]),
    import('emojibase-data/fi/messages.json').then((m) => m.default as RawMessages),
  ]);
  return { data, messages };
}

async function loadEn(): Promise<{ data: RawEmoji[]; messages: RawMessages }> {
  const [data, messages] = await Promise.all([
    import('emojibase-data/en/data.json').then((m) => m.default as RawEmoji[]),
    import('emojibase-data/en/messages.json').then((m) => m.default as RawMessages),
  ]);
  return { data, messages };
}

function buildEntry(raw: RawEmoji): EmojiEntry {
  const variants = raw.skins
    ?.filter((s) => typeof s.tone === 'number')
    .sort((a, b) => (a.tone as number) - (b.tone as number))
    .map((s) => s.emoji);
  const hasSkinTone = Boolean(variants && variants.length === 5);
  const entry: EmojiEntry = {
    emoji: raw.emoji,
    label: raw.label,
    group: '',
    subgroup: '',
    keywords: raw.tags ?? [],
    hasSkinTone,
  };
  if (hasSkinTone && variants) entry.skinToneVariants = variants;
  return entry;
}

function transform(data: RawEmoji[], messages: RawMessages, meta: RawGroupMeta): EmojiGroup[] {
  const groupKeyByIdx = meta.groups;
  const subgroupLabelByIdx = new Map<number, string>();
  messages.subgroups.forEach((s) => subgroupLabelByIdx.set(s.order, s.message));

  const groupLabelByKey = new Map<string, string>();
  messages.groups.forEach((g) => groupLabelByKey.set(g.key, g.message));

  const orderedGroups = [...messages.groups].sort((a, b) => a.order - b.order);
  const grouped = new Map<string, EmojiEntry[]>();
  orderedGroups.forEach((g) => {
    if (g.key !== 'component') grouped.set(g.key, []);
  });

  for (const raw of data) {
    if (raw.group === undefined) continue;
    if (raw.group === SKIN_TONE_COMPONENT_GROUP) continue;
    const groupKey = groupKeyByIdx[String(raw.group)];
    if (!groupKey) continue;
    const bucket = grouped.get(groupKey);
    if (!bucket) continue;
    const entry = buildEntry(raw);
    entry.group = groupLabelByKey.get(groupKey) ?? groupKey;
    if (raw.subgroup !== undefined) {
      entry.subgroup = subgroupLabelByIdx.get(raw.subgroup) ?? '';
    }
    bucket.push(entry);
  }

  const groups: EmojiGroup[] = [];
  orderedGroups.forEach((g) => {
    const entries = grouped.get(g.key);
    if (!entries || entries.length === 0) return;
    groups.push({ key: g.key, label: g.message, entries });
  });
  return groups;
}

// Strip Unicode variation selectors so callers can look up labels with either
// the bare or qualified form (e.g. "🐈" vs "🐈️").
function normalize(emoji: string): string {
  return emoji.replace(/[︎️]/g, '');
}

function indexLabel(emoji: string, label: string): void {
  labelCache.set(emoji, label);
  const bare = normalize(emoji);
  if (bare !== emoji) labelCache.set(bare, label);
}

function indexLabels(groups: EmojiGroup[]): void {
  for (const group of groups) {
    for (const entry of group.entries) {
      indexLabel(entry.emoji, entry.label);
      if (entry.skinToneVariants) {
        entry.skinToneVariants.forEach((variant) => indexLabel(variant, entry.label));
      }
    }
  }
}

export async function loadEmojiGroups(locale: EmojiLocale): Promise<EmojiGroup[]> {
  const cached = groupCache.get(locale);
  if (cached) return cached;
  const { data, messages, meta } = await fetchRaw(locale);
  const groups = transform(data, messages, meta);
  groupCache.set(locale, groups);
  indexLabels(groups);
  return groups;
}

export function getEmojiLabel(emoji: string): string {
  return labelCache.get(emoji) ?? labelCache.get(normalize(emoji)) ?? emoji;
}

interface RankedHit {
  entry: EmojiEntry;
  rank: number;
}

export function searchEmojis(groups: EmojiGroup[], query: string): EmojiEntry[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return [];
  const seen = new Set<string>();
  const hits: RankedHit[] = [];
  for (const group of groups) {
    for (const entry of group.entries) {
      if (seen.has(entry.emoji)) continue;
      const label = entry.label.toLowerCase();
      let rank: number | null = null;
      if (label.startsWith(trimmed)) rank = 0;
      else if (label.includes(trimmed)) rank = 1;
      else if (entry.keywords.some((k) => k.toLowerCase().includes(trimmed))) rank = 2;
      if (rank === null) continue;
      seen.add(entry.emoji);
      hits.push({ entry, rank });
    }
  }
  hits.sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    if (a.entry.label.length !== b.entry.label.length) {
      return a.entry.label.length - b.entry.label.length;
    }
    return a.entry.label.localeCompare(b.entry.label);
  });
  return hits.map((h) => h.entry);
}

export function __resetEmojiCacheForTest(): void {
  groupCache.clear();
  labelCache.clear();
}
